import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, catchError, finalize, of, takeUntil } from 'rxjs';
import { CourseBundlePackage } from '../../models/course-bundle-package.model';
import { CourseBundlePackageService } from '../../services/course-bundle-package.service';
import { SpinnerService } from '../../services/spinner.service';
import { ToasterService } from '../../services/toaster.service';
import { PaymentLinkModalComponent } from '../payment-link-modal/payment-link-modal.component';

/** Passed by a PARENT opening the wizard from a child's profile; omitted for a student's own header button. */
export interface PremiumUpgradeWizardData {
  studentId?: string;
}

/**
 * Guided "Upgrade to Premium" flow opened from the header button, replacing a straight
 * navigation to /manage-package/available-package. Three steps — value prop, plan details,
 * review & confirm — ending in the same real purchase/payment-link calls Manage Package uses,
 * so the Stripe handoff and success/cancel behavior are unchanged.
 */
@Component({
  selector: 'mindbase-premium-upgrade-wizard',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatIconModule, MatTooltipModule, TranslateModule],
  templateUrl: './premium-upgrade-wizard.component.html',
  styleUrl: './premium-upgrade-wizard.component.scss',
})
export class PremiumUpgradeWizardComponent implements OnInit, OnDestroy {
  private readonly dialogRef = inject(MatDialogRef<PremiumUpgradeWizardComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly packageService = inject(CourseBundlePackageService);
  private readonly toasterService = inject(ToasterService);
  private readonly spinnerService = inject(SpinnerService);
  private readonly destroy$ = new Subject<void>();
  private readonly data = inject<PremiumUpgradeWizardData | null>(MAT_DIALOG_DATA, { optional: true });
  /** The child a PARENT is upgrading — premium is bought for them and the purchase is validated server-side. */
  protected readonly studentId: string | undefined = this.data?.studentId;

  protected step: 1 | 2 | 3 = 1;
  protected loading = true;
  protected plan: CourseBundlePackage | undefined;
  protected coursesOpen = false;
  protected termsAccepted = false;
  protected purchasing = false;
  protected generatingLink = false;

  ngOnInit(): void {
    const loaderKey = 'premiumUpgradeWizardPackages';
    this.spinnerService.addToLoader(loaderKey);
    this.packageService
      .getActivePackages(this.studentId)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of([] as CourseBundlePackage[])),
        finalize(() => {
          this.loading = false;
          this.spinnerService.removeFromLoader(loaderKey);
        }),
      )
      .subscribe((packages) => {
        const premiumPlans = (packages ?? []).filter((p) => p.isPremium);
        this.plan = premiumPlans.find((p) => p.expiryType === 'yearly') ?? premiumPlans[0];
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected next(): void {
    if (this.step < 3) this.step = (this.step + 1) as 1 | 2 | 3;
  }

  protected back(): void {
    if (this.step > 1) this.step = (this.step - 1) as 1 | 2 | 3;
  }

  protected toggleCourses(): void {
    this.coursesOpen = !this.coursesOpen;
  }

  /** Same bullet-splitting rule as Manage Package's `descriptionPoints()`. */
  protected get planBullets(): string[] {
    return (this.plan?.description ?? '')
      .split(/\r?\n|•/)
      .map((line) => line.replace(/^[-*]\s*/, '').trim())
      .filter((line) => line.length > 0);
  }

  protected courseProviderLabel(courseType?: string): string {
    switch ((courseType ?? '').toLowerCase()) {
      case 'udemy': return 'Udemy';
      case 'scorm': return 'SCORM';
      case 'custom': return 'Custom';
      default: return courseType ? courseType.charAt(0).toUpperCase() + courseType.slice(1) : '';
    }
  }

  /** Step 1 "What you unlock" list — static marketing copy for the partner libraries Premium opens up. */
  protected readonly partners: { name: string; desc: string }[] = [
    { name: 'Codio', desc: 'Interactive coding courses — write real code for Programming, Game Design, and STEM.' },
    { name: 'Labster', desc: "Virtual science labs — run experiments you can't do at school, for Medicine, Health and Science." },
    { name: 'Edmentum', desc: 'Subject courses — strengthen the maths, science and English your pathway needs.' },
    { name: 'Udemy', desc: 'Skills courses — go deeper on any topic across all pathways.' },
    { name: 'ProjectSet', desc: 'Real-world projects — build more portfolio work to talk about in interviews.' },
    { name: 'Body Interact', desc: 'More simulations specific to Medicine, Health Sciences, and STEM with real patients scenarios.' },
  ];

  protected purchase(): void {
    if (!this.plan || this.purchasing || !this.termsAccepted) return;
    this.purchasing = true;
    this.packageService
      .purchase(this.plan._id, this.studentId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.purchasing = false;
        }),
      )
      .subscribe({
        next: (res) => {
          if (res?.paymentUrl) {
            window.location.href = res.paymentUrl;
            return;
          }
          if (res?.resumed) {
            this.toasterService.successToastr("Your existing subscription's cancellation has been undone.");
          } else {
            this.toasterService.successToastr('Package purchased successfully');
          }
          this.dialogRef.close(true);
        },
      });
  }

  protected generatePaymentLink(): void {
    if (!this.plan || this.generatingLink) return;
    this.generatingLink = true;
    this.packageService
      .generatePackagePaymentLink(this.plan._id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.generatingLink = false;
        }),
      )
      .subscribe({
        next: (res) => {
          this.dialog.open(PaymentLinkModalComponent, {
            width: '480px',
            maxWidth: '100vw',
            disableClose: false,
            data: { paymentLinkId: res.paymentLinkId, url: res.paymentLinkUrl, expiresAt: res.expiresAt, kind: 'package' },
          });
        },
      });
  }

  protected close(): void {
    this.dialogRef.close();
  }
}
